/**
 * useLimitCheck Hook
 * Check product/feature limits before allowing creation actions
 * Shows appropriate upgrade modal if limit is exceeded
 */

import { useState, useCallback } from 'react';
import { billingApi, featuresApi } from '../services/api';

export function useLimitCheck() {
  const [isChecking, setIsChecking] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitType, setLimitType] = useState('products');
  const [currentUsage, setCurrentUsage] = useState(null);
  const [currentLimit, setCurrentLimit] = useState(null);
  
  /**
   * Check if user can create a new product
   * @returns {Promise<{canCreate: boolean, usage: object}>}
   */
  const checkProductLimit = useCallback(async () => {
    setIsChecking(true);
    try {
      // Get limits from billing endpoint (consolidated)
      const response = await billingApi.getLimits();
      const data = response.data;
      
      const productData = data.products || {};
      const used = productData.used || 0;
      const limit = productData.limit;
      
      // Check if limit exceeded
      if (limit && used >= limit) {
        // Limit exceeded - show modal
        setLimitType('products');
        setCurrentUsage(used);
        setCurrentLimit(limit);
        setShowUpgradeModal(true);
        return { canCreate: false, usage: productData };
      }
      
      return { canCreate: true, usage: productData };
    } catch (error) {
      console.error('Error checking product limit:', error);
      // If check fails, allow creation and let server handle it
      return { canCreate: true, usage: null };
    } finally {
      setIsChecking(false);
    }
  }, []);
  
  /**
   * Check if user can create new features
   * @param {number} featureCount - Number of features to create (default: 1)
   * @returns {Promise<{canCreate: boolean, usage: object}>}
   */
  const checkFeatureLimit = useCallback(async (featureCount = 1) => {
    setIsChecking(true);
    try {
      const response = await featuresApi.getUsage();
      const data = response.data;
      
      // Check if adding this many features would exceed the limit
      if (data.limit && data.remaining < featureCount) {
        setLimitType('features');
        setCurrentUsage(data.used);
        setCurrentLimit(data.limit);
        setShowUpgradeModal(true);
        return { canCreate: false, usage: data };
      }
      
      return { canCreate: true, usage: data };
    } catch (error) {
      console.error('Error checking feature limit:', error);
      return { canCreate: true, usage: null };
    } finally {
      setIsChecking(false);
    }
  }, []);
  
  /**
   * Generic limit check that fetches context
   * @param {string} type - 'products' or 'features'
   * @returns {Promise<{canCreate: boolean, context: object}>}
   */
  const checkLimit = useCallback(async (type = 'products') => {
    setIsChecking(true);
    try {
      const response = await billingApi.getUpgradeContext(type);
      const context = response.data;
      
      // Check if limit is exceeded
      if (context.current_limit && context.current_usage >= context.current_limit) {
        setLimitType(type);
        setCurrentUsage(context.current_usage);
        setCurrentLimit(context.current_limit);
        setShowUpgradeModal(true);
        return { canCreate: false, context };
      }
      
      return { canCreate: true, context };
    } catch (error) {
      console.error('Error checking limit:', error);
      return { canCreate: true, context: null };
    } finally {
      setIsChecking(false);
    }
  }, []);
  
  const closeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);
  
  return {
    isChecking,
    showUpgradeModal,
    limitType,
    currentUsage,
    currentLimit,
    checkProductLimit,
    checkFeatureLimit,
    checkLimit,
    closeModal,
    setShowUpgradeModal,
  };
}

export default useLimitCheck;
